/**
 * ImportHistoryDialog — import de l'historique Excel/CSV de mandats.
 * Flux : télécharger le modèle → déposer le fichier → aperçu validé
 * (valides / erreurs / doublons) → confirmation → rapport.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { FileUp, Download, Loader2, CheckCircle2, AlertTriangle, Upload, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function ImportHistoryDialog({ open, onClose, onImported }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [rollingBack, setRollingBack] = useState(null);
    const inputRef = useRef(null);

    const reset = () => { setFile(null); setPreview(null); setReport(null); };

    const loadHistory = async () => {
        try {
            const r = await api.get('/pilotage/import/history');
            setHistory(r.data.imports || []);
        } catch (e) { /* silencieux */ }
    };

    useEffect(() => { if (open) loadHistory(); }, [open]);

    const rollback = async (batch) => {
        if (!window.confirm(
            `Annuler le lot ${batch.batch_id} ? Les ${batch.created} mandat(s) importé(s) `
            + 'et leurs mouvements seront supprimés définitivement.')) return;
        setRollingBack(batch.batch_id);
        try {
            const r = await api.post(`/pilotage/import/rollback/${batch.batch_id}`);
            toast.success(`Lot annulé — ${r.data.deleted_mandates} mandat(s) supprimé(s)`);
            loadHistory();
            onImported?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Annulation impossible");
        } finally { setRollingBack(null); }
    };

    const downloadTemplate = async () => {
        try {
            const r = await api.get('/pilotage/import/template', { responseType: 'blob' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(r.data);
            a.download = 'homly-modele-import-mandats.xlsx';
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (e) { toast.error('Téléchargement du modèle impossible'); }
    };

    const analyze = async (f) => {
        setBusy(true);
        setPreview(null);
        setReport(null);
        try {
            const fd = new FormData();
            fd.append('file', f);
            const r = await api.post('/pilotage/import/preview', fd);
            setPreview(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fichier illisible');
            setFile(null);
        } finally { setBusy(false); }
    };

    const apply = async () => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/pilotage/import/apply', fd);
            setReport(r.data);
            toast.success(`${r.data.created} mandat(s) importé(s)`);
            loadHistory();
            onImported?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec de l'import");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
            <DialogContent className="max-w-lg" data-testid="import-history-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileUp className="w-4 h-4" style={{ color: '#681793' }} />
                        Importer l'historique de mandats
                    </DialogTitle>
                    <DialogDescription>
                        Reprenez vos anciens tableaux Excel : mandats, ventes et sorties sont
                        intégrés au journal des mouvements avec leurs vraies dates.
                    </DialogDescription>
                </DialogHeader>

                {report ? (
                    <div className="text-center py-4 space-y-2" data-testid="import-report">
                        <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
                        <p className="font-bold text-lg">{report.created} mandat(s) importé(s)</p>
                        <p className="text-xs text-muted-foreground">
                            Lot {report.batch_id} · {report.skipped} ignoré(s) (déjà présents)
                            {preview && ` · ${preview.summary.errors} ligne(s) en erreur non importée(s)`}
                        </p>
                        <button type="button" onClick={() => { reset(); onClose(); }}
                                data-testid="import-close-btn"
                                className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                                style={{ background: '#681793' }}>
                            Voir le pilotage mis à jour
                        </button>
                    </div>
                ) : (
                    <>
                        <button type="button" onClick={downloadTemplate}
                                data-testid="import-template-btn"
                                className="flex items-center gap-2 text-sm font-medium hover:underline"
                                style={{ color: '#681793' }}>
                            <Download className="w-4 h-4" /> Télécharger le modèle Excel
                        </button>

                        <div
                            className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition hover:border-[#681793]"
                            style={{ borderColor: 'var(--border)' }}
                            onClick={() => inputRef.current?.click()}
                            data-testid="import-dropzone"
                        >
                            <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden"
                                   data-testid="import-file-input"
                                   onChange={(e) => {
                                       const f = e.target.files?.[0];
                                       if (f) { setFile(f); analyze(f); }
                                   }} />
                            {busy ? (
                                <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#681793' }} />
                            ) : (
                                <>
                                    <Upload className="w-6 h-6 mx-auto mb-1.5 text-muted-foreground" />
                                    <p className="text-sm font-medium">
                                        {file ? file.name : 'Déposez votre fichier .xlsx ou .csv'}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">Max 2000 lignes · 5 Mo</p>
                                </>
                            )}
                        </div>

                        {preview && (
                            <div className="space-y-2" data-testid="import-preview">
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div className="rounded-lg p-2 bg-emerald-500/10">
                                        <p className="text-lg font-bold text-emerald-600" data-testid="import-valid-count">{preview.summary.valid}</p>
                                        <p className="text-[10px] uppercase text-muted-foreground">Valides</p>
                                    </div>
                                    <div className="rounded-lg p-2 bg-red-500/10">
                                        <p className="text-lg font-bold text-red-600">{preview.summary.errors}</p>
                                        <p className="text-[10px] uppercase text-muted-foreground">Erreurs</p>
                                    </div>
                                    <div className="rounded-lg p-2 bg-amber-500/10">
                                        <p className="text-lg font-bold text-amber-600">{preview.summary.duplicates}</p>
                                        <p className="text-[10px] uppercase text-muted-foreground">Doublons</p>
                                    </div>
                                </div>
                                {preview.errors.length > 0 && (
                                    <div className="max-h-32 overflow-y-auto rounded-lg border p-2 text-xs space-y-1"
                                         style={{ borderColor: 'var(--border)' }}>
                                        {preview.errors.slice(0, 20).map((e, i) => (
                                            <p key={i} className="flex gap-1.5">
                                                <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                                                <span>Ligne {e.line} ({e.mandate_number || '—'}) : {e.errors.join(', ')}</span>
                                            </p>
                                        ))}
                                    </div>
                                )}
                                <button type="button" onClick={apply}
                                        disabled={busy || preview.summary.valid === 0}
                                        data-testid="import-apply-btn"
                                        className="w-full py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-50"
                                        style={{ background: 'linear-gradient(135deg, #681793, #9333ea)' }}>
                                    {busy ? 'Import en cours…' : `Importer ${preview.summary.valid} mandat(s)`}
                                </button>
                            </div>
                        )}

                        {history.length > 0 && (
                            <div className="pt-3 border-t" style={{ borderColor: 'var(--border)' }}
                                 data-testid="import-history-list">
                                <p className="text-[11px] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5"
                                   style={{ color: 'var(--muted-foreground)' }}>
                                    <History className="w-3.5 h-3.5" /> Historique des lots importés
                                </p>
                                <div className="max-h-44 overflow-y-auto space-y-1.5">
                                    {history.map((h) => (
                                        <div key={h.batch_id}
                                             data-testid={`import-batch-${h.batch_id}`}
                                             className="flex items-center gap-2 rounded-lg border px-3 py-2 text-xs"
                                             style={{ borderColor: 'var(--border)',
                                                      opacity: h.cancelled ? 0.55 : 1 }}>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold truncate">
                                                    {h.filename || `Lot ${h.batch_id}`}
                                                    {h.cancelled && (
                                                        <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase"
                                                              style={{ background: 'rgba(220,38,38,0.12)', color: '#dc2626' }}>
                                                            annulé
                                                        </span>
                                                    )}
                                                </p>
                                                <p style={{ color: 'var(--muted-foreground)' }}>
                                                    {h.applied_at ? new Date(h.applied_at).toLocaleDateString('fr-FR') : ''}
                                                    {' · '}{h.created} mandat(s)
                                                    {h.actor_name && ` · par ${h.actor_name}`}
                                                </p>
                                            </div>
                                            {!h.cancelled && h.created > 0 && (
                                                <button type="button"
                                                        onClick={() => rollback(h)}
                                                        disabled={rollingBack === h.batch_id}
                                                        data-testid={`import-rollback-${h.batch_id}`}
                                                        className="flex items-center gap-1 px-2 py-1 rounded-md border font-semibold shrink-0 disabled:opacity-50"
                                                        style={{ borderColor: 'rgba(220,38,38,0.4)', color: '#dc2626' }}>
                                                    {rollingBack === h.batch_id
                                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                                        : <Trash2 className="w-3 h-3" />}
                                                    Annuler
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
